// import api from '../api';
import axios from 'axios';

export const useNSWPointV2API = (
    apiKey,
    apiState,
    apiAddressType,
    apiDataSet,
    apiOutFields,
    apiMaxResults = 10,
) => {
    let currentRequestNo = 0;
    const pointNSWDomain = 'https://point.digital.nsw.gov.au';

    const getLatestSuggestions = async value => {
        const { requestNo, suggestions } = await getSuggestions(
            value,
            ++currentRequestNo
        );

        return {
            suggestions,
            isLatest: requestNo === currentRequestNo,
        };
    };

    const getSuggestions = async (value, requestNo) => {
        let url = `${pointNSWDomain}/v2/api/predictive1?address=${value}&maxNumberOfResults=${apiMaxResults}`;

        if (apiState) {
            url += `&stateTerritory=${apiState}`;
        }
        if (apiAddressType) {
            url += `&addressType=${apiAddressType}`;
        }
        if (apiDataSet) {
            url += `&dataset=${apiDataSet}`;
        }

        const response = await axios({
            method: 'GET',
            url: url,
            headers: {
                'x-api-key': apiKey,
            },
        });
        const { data } = response;

        return {
            suggestions: data instanceof Array ? data : [],
            requestNo,
        };
    };

    const getDetailsById = async id => {
        apiKey = validatedApiKey(apiKey);
        const url = `${pointNSWDomain}/v2/api/predictive2`;
        let body = `{"id":"${id}"`;
        body += apiOutFields ?
            `, "outFields": ${JSON.stringify(apiOutFields)}}` :
            '}';

        const response = await axios( {
            url: url,
            method: 'POST',
            headers: {
                'x-api-key': apiKey,
            },
            data: body,
        });
        const { data, status } = response;

        return status === 200 ? convertAddress(data) : {};
    };

    const convertAddress = address => {
        const {
            data: { geo, addressDetails, addressId, localGovernmentArea, cadastralParcels },
        } = address;

        const {
            streetName,
            streetType,
            siteName,
            localityName,
            stateTerritory,
            postcode,
            formattedAddress,
            postalDeliveryType,
            postalDeliveryNumber,
        } = addressDetails;

        // check if we have localGovernmentArea first, then set LGA vars
        const lgaName = localGovernmentArea ?
            localGovernmentArea.lgaName :
            undefined;
        const lgaShortName = localGovernmentArea ?
            localGovernmentArea.lgaShortName :
            undefined;
        const lgaPid = localGovernmentArea ?
            localGovernmentArea.lgaPid :
            undefined;

        // check if we have a lat/long first, and then set the lat/long accordingly
        const latLong = geo &&
            geo.geometry &&
            geo.geometry.coordinates &&
            2 === geo.geometry.coordinates.length;
        const longitude = latLong ?
            geo.geometry.coordinates[0] :
            undefined;
        const latitude = latLong ?
            geo.geometry.coordinates[1] :
            undefined;

        // build the street address in correct order, from the following variables
        const propertyName = siteName;
        const buildingNumber = getIdentifier(addressDetails, true);
        const unitNumber = getIdentifier(addressDetails, false);
        const streetNumber = getStreetNumber(addressDetails);
        let streetAddress = '';

        streetAddress = propertyName || streetAddress;
        streetAddress = buildingNumber ? `${streetAddress} ${buildingNumber}` : streetAddress;
        streetAddress = unitNumber ? `${streetAddress} ${unitNumber}` : streetAddress;
        if (streetNumber && streetName && streetType) {
            streetAddress = streetAddress ?
                `${streetAddress} ${streetNumber} ${streetName} ${streetType}` :
                `${streetNumber} ${streetName} ${streetType}`;
        } else if (postalDeliveryType && postalDeliveryNumber) {
            streetAddress = streetAddress ?
                `${streetAddress} ${postalDeliveryType} ${postalDeliveryNumber}` :
                `${postalDeliveryType} ${postalDeliveryNumber}`;
        }

        return {
            unitNumber,
            buildingNumber,
            streetNumber: streetNumber,
            streetName,
            streetType,
            propertyName,
            postalDeliveryType,
            postalDeliveryNumber,
            streetAddress: streetAddress.trim(),
            suburb: localityName,
            state: stateTerritory,
            country: 'Australia',
            postcode,
            latitude,
            longitude,
            nswPointId: addressId,
            formattedAddress: formattedAddress,
            lgaName,
            lgaShortName,
            lgaPid,
            cadastralParcels,
            validated: true,
        };
    };

    const getIdentifier = (address, isBuilding) => {
        const {
            complexUnitType,
            complexLevelType,
            complexUnitIdentifier,
            complexLevelNumber,
        } = address;
        let identifier = '';

        if (complexUnitType == null && complexLevelType == null) {
            return undefined;
        }

        if (isBuilding && complexUnitType !== 'BUILDING') {
            return undefined;
        }

        if (!isBuilding && complexUnitType === 'BUILDING') {
            return undefined;
        }

        if (complexUnitType != null) {
            identifier += `${complexUnitType} ${complexUnitIdentifier}`;
        }

        if (complexLevelType != null) {
            identifier += ` ${complexLevelType} ${complexLevelNumber}`;
        }

        return identifier.trim();
    };

    const getStreetNumber = address => {
        const { streetNumber1, streetNumber2 } = address;
        if (streetNumber1 == null && streetNumber2 == null) {
            return undefined;
        }

        if (streetNumber1 != null && streetNumber2 != null) {
            return `${streetNumber1}-${streetNumber2}`;
        }
        return streetNumber1 || streetNumber2;
    };

    const validatedApiKey = apiKey => {
        if (apiKey) {
            return apiKey;
        } else {
            console.warn('No api key provided for the NSW Point API. Please request API key as per documention.');
            return '';
        }
    };

    return {
        getLatestSuggestions,
        getDetailsById
    };
};
